import React from "react";
import styled from "styled-components";

import {
  CANONICAL_LABELS,
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  DEFAULT_ALL_OF_LABELS,
  DEFAULT_EGG_TRANSFORM,
} from "../../constants";
import { consumeUnlabeledHandoff } from "../../handoff";
import {
  eggImageUrl,
  marcImageUrl,
  referenceFilename,
  referenceUrl,
} from "../../paths";
import { fetchOptionalJson } from "../../utils";
import { eggTransformToCss } from "../../virtualCoords";
import useLocalStorageState from "../../hooks/useLocalStorageState";
import useKey from "../../hooks/useKey";
import FitContainer from "../FitContainer";

const DEFAULT_ALL_OF_SET = new Set(
  DEFAULT_ALL_OF_LABELS.map((s) => s.toLowerCase()),
);

function defaultGroupMode(labelKey) {
  return DEFAULT_ALL_OF_SET.has(labelKey) ? "all" : "any";
}

// Seed labels + deletions to match a committed reference.json snapshot:
// labels come directly from committed regions; a region missing from the
// committed set (because it was deleted last session) is marked deleted.
// Group modes pulled from the committed `labelGroups` dict (only entries
// that diverge from the default-list need to be kept).
function seedFromCommitted(
  committed,
  unlabeled,
  setLabels,
  setDeletedIds,
  setGroupModes,
) {
  const committedById = new Map(
    (committed.drawing?.regions ?? []).map((r) => [r.id, r]),
  );
  const nextLabels = {};
  const nextDeleted = [];
  for (const r of unlabeled.drawing.regions) {
    const c = committedById.get(r.id);
    if (!c) {
      nextDeleted.push(r.id);
    } else if ((c.label ?? "").trim().length > 0) {
      nextLabels[r.id] = c.label;
    }
  }
  setLabels(nextLabels);
  setDeletedIds(nextDeleted);
  setGroupModes(committed.labelGroups ?? {});
}

function LabelApp({ marcId }) {
  const [reference, setReference] = React.useState(null);
  // Committed reference (reference.json) — used to seed labels + deletions
  // on first load and to support a "reset to committed" button. Null if the
  // file doesn't exist yet (fresh marcId with no labels ever committed).
  const [committed, setCommitted] = React.useState(null);
  const [loadError, setLoadError] = React.useState(null);
  const [labels, setLabels] = useLocalStorageState(
    `egg-game:v1:marc:${marcId}:labels`,
    {},
  );
  const [deletedIds, setDeletedIds] = useLocalStorageState(
    `egg-game:v1:marc:${marcId}:deletedRegions`,
    [],
  );
  // Per-label "all" / "any" overrides. Empty by default — labels not in
  // this dict resolve via `defaultGroupMode` (eyes/eyebrows/pupils → all,
  // everything else → any).
  const [groupModes, setGroupModes] = useLocalStorageState(
    `egg-game:v1:marc:${marcId}:groupModes`,
    {},
  );
  const [selectedId, setSelectedId] = React.useState(null);
  const [showAll, setShowAll] = React.useState(false);
  // Whether this label session arrived via author-mode handoff. When true,
  // region IDs come from a fresh trace and don't correspond to whatever's
  // in the committed file — so we wipe pre-existing labels/deletions and
  // disable the "Reset to committed" button (which would mis-apply old
  // labels to new IDs).
  const [fromHandoff, setFromHandoff] = React.useState(false);
  const seededRef = React.useRef(false);
  const canvasRef = React.useRef(null);
  const maskCache = React.useRef({});
  const labelInputRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    // Two sources of truth:
    //
    //   1. sessionStorage handoff — author mode just saved and navigated
    //      here, the unlabeled JSON is in memory.
    //   2. referenceUrl(marcId) on disk — the committed file. Used as the
    //      committed-labels source (for seeding) when a handoff is
    //      present, OR as the working copy itself when there's no
    //      handoff (e.g. you reloaded the label tab to keep editing).
    //
    // There's no separate reference-unlabeled.json on disk anymore — the
    // handoff is the only intermediate artifact.
    const handoffJson = consumeUnlabeledHandoff(marcId);
    const committedRefPromise = fetchOptionalJson(referenceUrl(marcId));

    let unlabeledPromise;
    if (handoffJson) {
      // Fresh trace from author mode. Region IDs are positional ("biggest
      // region in this trace = r-001"), so previously-saved labels and
      // deletions can't be trusted to mean the same thing on the new
      // trace — wipe them and start clean. seededRef → true so the seed
      // effect below short-circuits.
      setFromHandoff(true);
      setLabels({});
      setDeletedIds([]);
      setGroupModes({});
      seededRef.current = true;
      try {
        unlabeledPromise = Promise.resolve(JSON.parse(handoffJson));
      } catch (err) {
        unlabeledPromise = Promise.reject(
          new Error(`handoff JSON: ${err.message}`),
        );
      }
    } else {
      // No handoff → continue editing the committed file in place.
      unlabeledPromise = committedRefPromise.then((ref) => {
        if (!ref) {
          throw new Error(
            `No ${referenceFilename(marcId)} on disk and no author handoff. Author the marc first.`,
          );
        }
        return ref;
      });
    }

    Promise.all([unlabeledPromise, committedRefPromise])
      .then(([unlabeled, committedRef]) => {
        if (cancelled) return;
        setReference(unlabeled);
        setCommitted(committedRef);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [marcId]);

  // Seed labels + deletions from the committed reference.json on first
  // mount, but only if localStorage is empty. A returning session with
  // in-progress edits keeps those edits; the "Reset to committed" button
  // below lets you force-reseed.
  React.useEffect(() => {
    if (seededRef.current) return;
    if (!reference) return;
    seededRef.current = true;

    const hasLocalLabels = Object.values(labels).some(
      (v) => (v ?? "").trim().length > 0,
    );
    const hasLocalDeletions = deletedIds.length > 0;
    if (hasLocalLabels || hasLocalDeletions) return;
    if (!committed) return;

    seedFromCommitted(
      committed,
      reference,
      setLabels,
      setDeletedIds,
      setGroupModes,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, committed]);

  const resetToCommitted = () => {
    if (!committed) {
      window.alert("No reference.json found to reset from.");
      return;
    }
    if (
      !window.confirm(
        "Reset labels and deletions to what's in the committed reference.json?",
      )
    ) {
      return;
    }
    seedFromCommitted(
      committed,
      reference,
      setLabels,
      setDeletedIds,
      setGroupModes,
    );
  };

  const visibleRegions = React.useMemo(() => {
    if (!reference) return [];
    const deleted = new Set(deletedIds);
    return reference.drawing.regions.filter((r) => !deleted.has(r.id));
  }, [reference, deletedIds]);

  // Default select the first region once regions are available
  React.useEffect(() => {
    if (selectedId == null && visibleRegions.length > 0) {
      setSelectedId(visibleRegions[0].id);
    }
  }, [visibleRegions, selectedId]);

  // Preload mask images
  React.useEffect(() => {
    if (!reference) return;
    reference.drawing.regions.forEach((r) => {
      if (maskCache.current[r.id]) return;
      const img = new Image();
      img.onload = () => {
        maskCache.current[r.id] = img;
        // Force redraw when each mask finishes loading
        drawCanvas();
      };
      img.src = r.mask;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const drawCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !reference) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const toDraw = showAll
      ? visibleRegions
      : visibleRegions.filter((r) => r.id === selectedId);

    for (const r of toDraw) {
      const maskImg = maskCache.current[r.id];
      if (!maskImg) continue;
      const { x, y, w, h } = r.bbox;
      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const octx = off.getContext("2d");
      octx.drawImage(maskImg, 0, 0);
      octx.globalCompositeOperation = "source-in";
      octx.fillStyle = r.color;
      octx.fillRect(0, 0, w, h);
      ctx.drawImage(off, x, y);
    }
  }, [reference, visibleRegions, selectedId, showAll]);

  React.useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const selectedRegion =
    visibleRegions.find((r) => r.id === selectedId) || null;
  const selectedIndex = visibleRegions.findIndex((r) => r.id === selectedId);

  const advance = React.useCallback(
    (dir) => {
      if (visibleRegions.length === 0) return;
      const i = Math.max(0, selectedIndex);
      const next = (i + dir + visibleRegions.length) % visibleRegions.length;
      setSelectedId(visibleRegions[next].id);
    },
    [visibleRegions, selectedIndex],
  );

  useKey("ArrowDown", () => advance(1), { preventDefault: true });
  useKey("ArrowUp", () => advance(-1), { preventDefault: true });

  const handleLabelChange = (value) => {
    if (!selectedRegion) return;
    setLabels((prev) => ({ ...prev, [selectedRegion.id]: value }));
  };

  const handleDelete = () => {
    if (!selectedRegion) return;
    const ok = window.confirm(
      `Delete region ${selectedRegion.id} (${selectedRegion.pixelCount} px)?`,
    );
    if (!ok) return;
    setDeletedIds((prev) => [...prev, selectedRegion.id]);
    // Move selection to next available
    const idx = visibleRegions.findIndex((r) => r.id === selectedRegion.id);
    const nextList = visibleRegions.filter((r) => r.id !== selectedRegion.id);
    if (nextList.length === 0) {
      setSelectedId(null);
    } else {
      setSelectedId(nextList[Math.min(idx, nextList.length - 1)].id);
    }
  };

  const handleUndelete = () => {
    setDeletedIds([]);
  };

  const handleClearLabels = () => {
    if (!window.confirm("Clear all labels?")) return;
    setLabels({});
  };

  // Plural labels = labels assigned to ≥2 visible regions. These are the
  // groups that get an explicit all/any toggle in the sidebar (singletons
  // can't have a group mode that matters).
  const pluralGroups = React.useMemo(() => {
    const counts = new Map();
    visibleRegions.forEach((r) => {
      const key = (labels[r.id] ?? "").trim().toLowerCase();
      if (!key) return;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .filter(([_, n]) => n >= 2)
      .map(([key, count]) => ({
        key,
        count,
        mode: groupModes[key] ?? defaultGroupMode(key),
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [visibleRegions, labels, groupModes]);

  const setGroupMode = (key, mode) => {
    setGroupModes((prev) => ({ ...prev, [key]: mode }));
  };

  const handleExport = () => {
    if (!reference) return;
    const regions = reference.drawing.regions
      .filter((r) => !deletedIds.includes(r.id))
      .map((r) => ({ ...r, label: labels[r.id] ?? r.label ?? "" }));
    // Bake the resolved mode for every plural label so consumers don't have
    // to re-implement the default-list. Singletons are omitted (mode is
    // irrelevant for them) to keep the file small.
    const labelGroups = {};
    pluralGroups.forEach((g) => {
      labelGroups[g.key] = g.mode;
    });
    const out = {
      ...reference,
      drawing: { ...reference.drawing, regions },
      labelGroups,
    };
    const json = JSON.stringify(out, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = referenceFilename(marcId);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const labeledCount = visibleRegions.filter(
    (r) => (labels[r.id] ?? "").trim().length > 0,
  ).length;

  if (loadError) {
    return (
      <Main>
        <ErrorBox>Failed to load reference: {loadError}</ErrorBox>
      </Main>
    );
  }

  if (!reference) {
    return (
      <Main>
        <LoadingBox>Loading reference…</LoadingBox>
      </Main>
    );
  }

  const eggTransform = reference.egg.transform ?? DEFAULT_EGG_TRANSFORM;
  const marcTransform = reference.marc?.transform;
  const marcStyle = marcTransform
    ? {
        transform: `translate(-50%, -50%) translate(${marcTransform.tx}px, ${marcTransform.ty}px) rotate(${marcTransform.rot}deg) scale(${marcTransform.scale})`,
      }
    : undefined;

  return (
    <Main>
      <Header>
        <Title>Marc Andreessen Egg Game</Title>
        <Badge>label · marc {marcId}</Badge>
        <HeaderStatus>
          {labeledCount} / {visibleRegions.length} labeled
          {deletedIds.length > 0 && ` · ${deletedIds.length} deleted`}
        </HeaderStatus>
      </Header>

      <Body>
        <Panes>
          <Pane>
            <FitContainer width={CANVAS_WIDTH} height={CANVAS_HEIGHT}>
              <Frame>
                <EggImg
                  src={eggImageUrl(reference.egg.id)}
                  alt=""
                  draggable={false}
                  style={{ transform: eggTransformToCss(eggTransform) }}
                />
                <RegionCanvas
                  ref={canvasRef}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                />
              </Frame>
            </FitContainer>
          </Pane>
          <Pane>
            <FitContainer width={CANVAS_WIDTH} height={CANVAS_HEIGHT}>
              <RefFrame>
                <RefImg
                  src={marcImageUrl(marcId)}
                  alt=""
                  draggable={false}
                  style={marcStyle}
                  $transformed={!!reference.marc?.transform}
                />
              </RefFrame>
            </FitContainer>
          </Pane>
        </Panes>

        <Sidebar>
          <SidebarHeader>
            <ShowAllRow>
              <label>
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                />{" "}
                Show all regions
              </label>
            </ShowAllRow>
          </SidebarHeader>

          <RegionList>
            {visibleRegions.map((r) => {
              const label = labels[r.id] ?? "";
              const isSelected = r.id === selectedId;
              return (
                <RegionRow
                  key={r.id}
                  $selected={isSelected}
                  onClick={() => setSelectedId(r.id)}
                >
                  <Swatch style={{ background: r.color }} />
                  <RegionId>{r.id}</RegionId>
                  <RegionPx>{r.pixelCount}px</RegionPx>
                  <RegionLabel $empty={!label.trim()}>
                    {label.trim() || "unlabeled"}
                  </RegionLabel>
                </RegionRow>
              );
            })}
          </RegionList>

          {pluralGroups.length > 0 && (
            <Groups>
              <GroupsHeader>Region groups</GroupsHeader>
              {pluralGroups.map((g) => (
                <GroupRow key={g.key}>
                  <GroupName>
                    {g.key} <GroupCount>×{g.count}</GroupCount>
                  </GroupName>
                  <GroupToggle role="group">
                    <GroupToggleButton
                      type="button"
                      $active={g.mode === "all"}
                      onClick={() => setGroupMode(g.key, "all")}
                      title="All subregions must be drawn individually"
                    >
                      all
                    </GroupToggleButton>
                    <GroupToggleButton
                      type="button"
                      $active={g.mode === "any"}
                      onClick={() => setGroupMode(g.key, "any")}
                      title="Hits pool across subregions; one well-drawn subregion clears the group"
                    >
                      any
                    </GroupToggleButton>
                  </GroupToggle>
                </GroupRow>
              ))}
            </Groups>
          )}

          {selectedRegion && (
            <Editor>
              <EditorHeader>
                <Swatch style={{ background: selectedRegion.color }} />
                <strong>{selectedRegion.id}</strong>
                <span>
                  {selectedRegion.pixelCount}px · color slot{" "}
                  {selectedRegion.colorIndex + 1}
                </span>
              </EditorHeader>
              <LabelInput
                ref={labelInputRef}
                type="text"
                placeholder="Label (e.g. left eye)"
                value={labels[selectedRegion.id] ?? ""}
                onChange={(e) => handleLabelChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    advance(1);
                  }
                }}
                autoFocus
              />
              <Suggestions>
                {CANONICAL_LABELS.map((s) => (
                  <SuggestChip
                    key={s}
                    type="button"
                    onClick={() => {
                      handleLabelChange(s);
                      advance(1);
                    }}
                  >
                    {s}
                  </SuggestChip>
                ))}
              </Suggestions>
              <EditorActions>
                <DangerButton type="button" onClick={handleDelete}>
                  Delete region
                </DangerButton>
              </EditorActions>
            </Editor>
          )}

          <Footer>
            <FooterButton type="button" onClick={handleExport}>
              Export {referenceFilename(marcId)}
            </FooterButton>
            <FooterRow>
              {deletedIds.length > 0 && (
                <MiniButton type="button" onClick={handleUndelete}>
                  Restore {deletedIds.length} deleted
                </MiniButton>
              )}
              <MiniButton type="button" onClick={handleClearLabels}>
                Clear labels
              </MiniButton>
              <MiniButton
                type="button"
                onClick={resetToCommitted}
                disabled={!committed || fromHandoff}
                title={
                  fromHandoff
                    ? "New trace from author mode — region IDs don't correspond to the committed file's regions, so a reset would mis-apply old labels"
                    : committed
                      ? "Replace current labels/deletions with what's in the committed reference"
                      : "No committed reference on disk to reset from"
                }
              >
                Reset to committed
              </MiniButton>
            </FooterRow>
            <Hint>
              ↑/↓ navigate · Enter = next · click a chip to label &amp; advance
              {committed
                ? " · editing on top of committed reference.json"
                : " · no committed reference.json yet"}
            </Hint>
          </Footer>
        </Sidebar>
      </Body>
    </Main>
  );
}

const Main = styled.main`
  height: 100vh;
  height: 100svh;
  display: flex;
  flex-direction: column;
  background: #f6f5f1;
  color: #1a1a1a;
  overflow: hidden;
`;

const Header = styled.header`
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 14px 24px;
  border-bottom: 1px solid #e4e2dc;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const Badge = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #2d5fa0;
`;

const HeaderStatus = styled.span`
  font-size: 13px;
  color: #555;
  margin-left: auto;
  font-variant-numeric: tabular-nums;
`;

const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 0;
  min-height: 0;
`;

const Panes = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  gap: 24px;
  padding: 24px;
  min-height: 0;
  overflow: hidden;
`;

const Pane = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Frame = styled.div`
  position: absolute;
  inset: 0;
  background: #ff8904;
  border-radius: 4px;
  overflow: hidden;
`;

const EggImg = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: center center;
  user-select: none;
  pointer-events: none;
  filter: brightness(0.8);
`;

const RegionCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const RefFrame = styled.div`
  position: absolute;
  inset: 0;
  background: #eceae4;
  border-radius: 4px;
  overflow: hidden;
`;

const RefImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  ${(p) =>
    p.$transformed
      ? `
    position: absolute;
    left: 50%;
    top: 50%;
    transform-origin: center center;
  `
      : `display: block;`}
`;

const Sidebar = styled.aside`
  border-left: 1px solid #e4e2dc;
  background: #fbfaf7;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const SidebarHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e4e2dc;
`;

const ShowAllRow = styled.div`
  font-size: 13px;
  label {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }
`;

const RegionList = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 6px 0;
`;

const RegionRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: 18px 60px 60px 1fr;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  border: none;
  background: ${(p) => (p.$selected ? "#e8e3d3" : "transparent")};
  cursor: pointer;
  text-align: left;
  font-size: 13px;

  &:hover {
    background: ${(p) => (p.$selected ? "#e8e3d3" : "#f0ede4")};
  }
`;

const Swatch = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: 1px solid #d0ccc2;
`;

const RegionId = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #555;
`;

const RegionPx = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  color: #888;
  text-align: right;
`;

const RegionLabel = styled.span`
  color: ${(p) => (p.$empty ? "#aaa" : "#1a1a1a")};
  font-style: ${(p) => (p.$empty ? "italic" : "normal")};
  font-weight: ${(p) => (p.$empty ? "400" : "600")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Groups = styled.div`
  border-top: 1px solid #e4e2dc;
  padding: 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const GroupsHeader = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #888;
`;

const GroupRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
`;

const GroupName = styled.span`
  font-weight: 600;
`;

const GroupCount = styled.span`
  color: #888;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  margin-left: 2px;
`;

const GroupToggle = styled.div`
  display: inline-flex;
  border: 1px solid #d0ccc2;
  border-radius: 4px;
  overflow: hidden;
`;

const GroupToggleButton = styled.button`
  font-size: 11px;
  padding: 3px 10px;
  border: none;
  background: ${(p) => (p.$active ? "#2d5fa0" : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#333")};
  cursor: pointer;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  & + & {
    border-left: 1px solid #d0ccc2;
  }
  &:hover {
    background: ${(p) => (p.$active ? "#26528d" : "#ece9e0")};
  }
`;

const Editor = styled.div`
  border-top: 1px solid #e4e2dc;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EditorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #555;
`;

const LabelInput = styled.input`
  padding: 8px 10px;
  border: 1px solid #d0ccc2;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  &:focus {
    outline: 2px solid #2d5fa0;
    outline-offset: -1px;
  }
`;

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const SuggestChip = styled.button`
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 99px;
  border: 1px solid #d0ccc2;
  background: #fff;
  cursor: pointer;
  &:hover {
    background: #ece9e0;
  }
`;

const EditorActions = styled.div`
  display: flex;
  gap: 6px;
`;

const DangerButton = styled.button`
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #c44;
  background: #fff;
  color: #c44;
  cursor: pointer;
  font-weight: 600;
  &:hover {
    background: #fbe9e9;
  }
`;

const Footer = styled.div`
  border-top: 1px solid #e4e2dc;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FooterButton = styled.button`
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid #2d5fa0;
  background: #2d5fa0;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: #26528d;
  }
`;

const FooterRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const MiniButton = styled.button`
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #d0ccc2;
  background: #fff;
  cursor: pointer;
  &:hover {
    background: #ece9e0;
  }
`;

const Hint = styled.div`
  font-size: 11px;
  color: #888;
`;

const LoadingBox = styled.div`
  padding: 40px;
  text-align: center;
  color: #555;
`;

const ErrorBox = styled.div`
  padding: 40px;
  color: #c44;
  code {
    background: #fbe9e9;
    padding: 2px 4px;
    border-radius: 2px;
  }
`;

export default LabelApp;
